//frontend\src\components\Matrizrepresentante.js

import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, TextField, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, InputLabel,
  FormControl, Snackbar, Collapse, Box, Avatar, Grid, Card, CardContent, Chip, TablePagination
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { Edit, ExpandMore, ExpandLess, Category, Close } from "@mui/icons-material";
import { Container } from "../components/ReqContainerStyles";
import SharePointManager from "./SharepointManager";
import { apiService, handleApiError } from '../config/api';
import RegistroCategorias from "./Registro_Modular";

const Alert = React.forwardRef((props, ref) => <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />);

// Componente de Simbología
const Simbologia = () => {
  const simbolos = [
    { color: "green", label: "VIGENTE" },
    { color: "yellow", label: "POR VENCER" },
    { color: "red", label: "VENCIDA" },
    { color: "gray", label: "INHABILITADO" }
  ];

  return (
    <Card variant="outlined" sx={{ p: 0.5 }}>
      <Typography variant="h7" sx={{ fontSize: "0.9rem", mb: 0.5 }}>Simbología</Typography>
      <CardContent sx={{ p: 0.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {simbolos.map((item, i) => (
            <Box key={item.label} sx={{ 
              display: "flex", 
              alignItems: "center", 
              height: "20px",
              borderRight: i < simbolos.length - 1 ? "1px solid #e0e0e0" : "none",
              pr: i < simbolos.length - 1 ? 0.75 : 0,
              mr: i < simbolos.length - 1 ? 0.75 : 0
            }}>
              <Avatar sx={{ bgcolor: item.color, width: 15, height: 15, minWidth: "15px" }} />
              <Typography sx={{ ml: 0.5, fontSize: "0.75rem" }}>{item.label}</Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const MatrizRepresentante = () => {
  // Estados principales
  const [representantes, setRepresentantes] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [asociaciones, setAsociaciones] = useState([]);
  const [cooperativas, setCooperativas] = useState([]);
  
  // Estados de UI
  const [filter, setFilter] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDepartamento, setFilterDepartamento] = useState("");
  const [filterMunicipio, setFilterMunicipio] = useState("");
  const [filterAsociacion, setFilterAsociacion] = useState("");
  const [filterFechaEleccion, setFilterFechaEleccion] = useState("");
  const [municipiosFiltroPorDepartamento, setMunicipiosFiltroPorDepartamento] = useState([]);
  
  // Estados de edición
  const [selectedRepresentante, setSelectedRepresentante] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [selectedDepartamento, setSelectedDepartamento] = useState("");
  const [municipiosFiltrados, setMunicipiosFiltrados] = useState([]);
  
  // Estados de control
  const [expandedRows, setExpandedRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [showRegistroCategorias, setShowRegistroCategorias] = useState(false);
  const [sharePointManager] = useState(new SharePointManager());

  // Inicialización de SharePoint (silenciosa)
  useEffect(() => {
    const initializeSharePointManager = async () => {
      try {
        await sharePointManager.initialize();
      } catch (error) {
        console.error("Error al inicializar SharePointManager:", error);
        // No mostramos alerta, solo registramos en consola
      }
    };
    initializeSharePointManager();
  }, [sharePointManager]);

  // Función para actualizar estados de representantes
  const updateRepresentantesStatus = async (updatedRepresentantes) => {
    try {
      for (const rep of updatedRepresentantes) {
        await apiService.representantes.update(rep.cod_representante, { estatus: rep.estatus });
      }
    } catch (error) {
      const errorMessage = handleApiError(error, "Error actualizando el estado de los representantes");
      setAlertMessage(errorMessage);
      setAlertSeverity("error");
      setOpenAlert(true);
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const datos = await apiService.utils.getDatosReporte();
        
        const today = new Date();
        const updatedRepresentantes = (datos.representantes || []).map((rep) => {
          const fechaVencimiento = new Date(rep.fecha_vencimiento);
          if (rep.estatus === "VIGENTE" && fechaVencimiento < today) {
            return { ...rep, estatus: "VENCIDA" };
          }
          return rep;
        });

        setRepresentantes(updatedRepresentantes);
        setDepartamentos(datos.departamentos || []);
        setMunicipios(datos.municipios || []);
        setAsociaciones(datos.asociaciones || []);
        setCooperativas(datos.cooperativas || []);

        const representantesParaActualizar = updatedRepresentantes.filter(rep => {
          const original = (datos.representantes || []).find(orig => orig.cod_representante === rep.cod_representante);
          return original && original.estatus !== rep.estatus;
        });

        if (representantesParaActualizar.length > 0) {
          await updateRepresentantesStatus(representantesParaActualizar);
        }
      } catch (error) {
        const errorMessage = handleApiError(error, "Error al cargar los datos");
        setAlertMessage(errorMessage);
        setAlertSeverity("error");
        setOpenAlert(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); 

  // Filtrar municipios por departamento
  useEffect(() => {
    if (filterDepartamento) {
      const filtered = municipios.filter(mun => mun.CodigoDepartamento === filterDepartamento);
      setMunicipiosFiltroPorDepartamento(filtered);
    } else {
      setMunicipiosFiltroPorDepartamento([]);
    }
  }, [filterDepartamento, municipios]);

  // Filtrar representantes
  const filteredRepresentantes = representantes.filter((rep) => {
    const matchAsociacion = !filterAsociacion || rep.cod_asociacion === filterAsociacion;
    const matchFecha = !filterFechaEleccion || rep.fecha_eleccion === filterFechaEleccion;
    const matchDepartamento = !filterDepartamento || rep.cod_depto === filterDepartamento;
    const matchMunicipio = !filterMunicipio || rep.cod_municipio === filterMunicipio;
    const matchSearch = !filter || 
      rep.nombre_representante?.toLowerCase().includes(filter.toLowerCase()) ||
      rep.dni?.includes(filter);
    const matchStatus = !filterStatus || rep.estatus === filterStatus;

    return matchAsociacion && matchFecha && matchSearch && matchDepartamento && matchMunicipio && matchStatus;
  });

  const handleEditClick = (representante) => {
    setSelectedRepresentante(representante);
    setEditedData({ ...representante });
    setSelectedDepartamento(representante.cod_depto);
    
    const municipiosFiltrados = municipios.filter(
      mun => mun.CodigoDepartamento === representante.cod_depto
    );
    setMunicipiosFiltrados(municipiosFiltrados);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData(prevData => ({ ...prevData, [name]: value }));
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditedData(prevData => ({
        ...prevData,
        fotografia: file,
        fotografiaPreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleFechaEleccionChange = (e) => {
    const fechaEleccion = e.target.value;
    
    if (fechaEleccion) {
      const fecha = new Date(fechaEleccion);
      fecha.setFullYear(fecha.getFullYear() + 3);
      const fechaVencimiento = fecha.toISOString().split('T')[0];
      
      if (editedData.estatus !== "INHABILITADO") {
        const hoy = new Date();
        const nuevoEstatus = fecha < hoy ? "VENCIDA" : "VIGENTE";
        
        setEditedData(prevData => ({
          ...prevData,
          fecha_eleccion: fechaEleccion,
          fecha_vencimiento: fechaVencimiento,
          estatus: nuevoEstatus
        }));
      } else {
        setEditedData(prevData => ({
          ...prevData,
          fecha_eleccion: fechaEleccion,
          fecha_vencimiento: fechaVencimiento
        }));
      }
    } else {
      setEditedData(prevData => ({ ...prevData, fecha_eleccion: '' }));
    }
  };

  const handleSaveClick = async () => {
    try {
      const camposModificados = {};
      let hayModificaciones = false;

      const camposProhibidos = ['categorias', 'cod_representante'];

      Object.keys(editedData).forEach(key => {
        if (key === 'fotografiaPreview' || camposProhibidos.includes(key)) return;
        
        if (key === 'fotografia' && editedData[key] instanceof File) {
          camposModificados[key] = editedData[key];
          hayModificaciones = true;
          return;
        }
        
        const valorOriginal = String(selectedRepresentante[key] || '');
        const valorEditado = String(editedData[key] || '');
        
        if (valorOriginal !== valorEditado) {
          camposModificados[key] = editedData[key];
          hayModificaciones = true;
        }
      });

      if (!hayModificaciones) {
        setAlertMessage("No hay cambios para guardar");
        setAlertSeverity("info");
        setOpenAlert(true);
        setSelectedRepresentante(null);
        return;
      }

      if (camposModificados.fotografia instanceof File) {
        try {
          const uploadResult = await sharePointManager.uploadFile(
            camposModificados.fotografia, 
            selectedRepresentante.cod_representante,
            selectedRepresentante.nombre_representante 
          );
          camposModificados.fotografia = uploadResult.webUrl;
        } catch (uploadError) {
          throw new Error("No se pudo subir la imagen");
        }
      }

      await apiService.representantes.update(
        selectedRepresentante.cod_representante, 
        camposModificados
      );

      await refreshRepresentantes();

      setAlertMessage("Representante actualizado con éxito");
      setAlertSeverity("success");
      setOpenAlert(true);
      setSelectedRepresentante(null);
      setEditedData({});
    } catch (error) {
      const errorMessage = handleApiError(error, "Error al actualizar representante");
      setAlertMessage(errorMessage);
      setAlertSeverity("error");
      setOpenAlert(true);
    }
  };

  const refreshRepresentantes = async () => {
    try {
      const data = await apiService.representantes.getAll();
      setRepresentantes(data);
    } catch (error) {
      const errorMessage = handleApiError(error, "Error al refrescar datos");
      setAlertMessage(errorMessage);
      setAlertSeverity("error");
      setOpenAlert(true);
    }
  };

  const toggleRowExpansion = (cod_representante) => {
    setExpandedRows(expandedRows.includes(cod_representante) ? [] : [cod_representante]);
  };

  const isPorVencer = (representante) => {
    const today = new Date();
    const oneMonthLater = new Date();
    oneMonthLater.setMonth(today.getMonth() + 1);
    const fechaVencimiento = new Date(representante.fecha_vencimiento);
    return fechaVencimiento > today && fechaVencimiento <= oneMonthLater;
  };

  const getStatusColor = (estatus, isPorVencer) => {
    if (isPorVencer) return "yellow";
    const colors = { VIGENTE: "green", VENCIDA: "red", INHABILITADO: "gray" };
    return colors[estatus] || "transparent";
  };

  const currentRepresentantes = filteredRepresentantes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (showRegistroCategorias) {
    return (
      <Container>
        <Button variant="outlined" onClick={() => { setShowRegistroCategorias(false); refreshRepresentantes(); }} sx={{ mb: 2 }}>
          ← Volver a edición de representantes
        </Button>
        <RegistroCategorias />
      </Container>
    );
  }

  return (
    <Container>
      <Snackbar open={openAlert} autoHideDuration={6000} onClose={() => setOpenAlert(false)}>
        <Alert onClose={() => setOpenAlert(false)} severity={alertSeverity}>{alertMessage}</Alert>
      </Snackbar>
      
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ color: '#432918', fontWeight: 'bold' }}>
          Matriz de Representantes
        </Typography>
        <Button
          variant="contained"
          startIcon={<Category />}
          onClick={() => setShowRegistroCategorias(true)}
          sx={{ bgcolor: '#b6a9a1', '&:hover': { bgcolor: '#2A1A0F' } }}
        >
          Gestionar Categorías
        </Button>
      </Box>
      
      {/* Filtros */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField label="Buscar Representante" variant="outlined" value={filter} 
            onChange={(e) => setFilter(e.target.value)} fullWidth placeholder="Buscar por nombre o DNI..." />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Asociación</InputLabel>
            <Select value={filterAsociacion} onChange={(e) => setFilterAsociacion(e.target.value)} label="Asociación">
              <MenuItem value="">Todas</MenuItem>
              {asociaciones.map((asoc) => (
                <MenuItem key={asoc.cod_asociacion} value={asoc.cod_asociacion}>{asoc.nombre_abreviado}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField label="Fecha de Elección" type="date" fullWidth value={filterFechaEleccion}
            onChange={(e) => setFilterFechaEleccion(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Departamento</InputLabel>
            <Select value={filterDepartamento} onChange={(e) => { setFilterDepartamento(e.target.value); setFilterMunicipio(""); }} label="Departamento">
              <MenuItem value="">Todos</MenuItem>
              {departamentos.map((depto) => (
                <MenuItem key={depto.CodigoDepartamento} value={depto.CodigoDepartamento}>{depto.NombreDepartamento}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Municipio</InputLabel>
            <Select value={filterMunicipio} onChange={(e) => setFilterMunicipio(e.target.value)} 
              label="Municipio" disabled={!filterDepartamento}>
              <MenuItem value="">Todos</MenuItem>
              {municipiosFiltroPorDepartamento.map((mun) => (
                <MenuItem key={mun.CodigoMunicipio} value={mun.CodigoMunicipio}>{mun.NombreMunicipio}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Estado</InputLabel>
            <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Estado">
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="VIGENTE">VIGENTE</MenuItem>
              <MenuItem value="VENCIDA">VENCIDA</MenuItem>
              <MenuItem value="INHABILITADO">INHABILITADO</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={8} md={5}>
          <Simbologia />
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {["Acciones", "Asociación", "Nombre", "DNI", "Municipio", "Departamento", "Categorías", "Estatus"].map(h => (
                <TableCell key={h} sx={{ fontWeight: "bold" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', p: 2 }}>
                  Cargando representantes...
                </TableCell>
              </TableRow>
            ) : (
              currentRepresentantes.map(rep => {
                const porVencer = isPorVencer(rep);
                const isExpanded = expandedRows.includes(rep.cod_representante);
                return (
                  <React.Fragment key={rep.cod_representante}>
                    <TableRow>
                      <TableCell>
                        <Grid container alignItems="center">
                          <Avatar sx={{ width: 20, height: 20, mr: 1, bgcolor: getStatusColor(rep.estatus, porVencer) }} />
                          <IconButton onClick={() => handleEditClick(rep)}><Edit color="primary" /></IconButton>
                          <IconButton onClick={() => toggleRowExpansion(rep.cod_representante)}>
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Grid>
                      </TableCell>
                      <TableCell>{asociaciones.find(a => a.cod_asociacion === rep.cod_asociacion)?.nombre_abreviado || "No seleccionado"}</TableCell>
                      <TableCell>{rep.nombre_representante}</TableCell>
                      <TableCell>{rep.dni}</TableCell>
                      <TableCell>{municipios.find(m => m.CodigoMunicipio === rep.cod_municipio)?.NombreMunicipio || "No seleccionado"}</TableCell>
                      <TableCell>{departamentos.find(d => d.CodigoDepartamento === rep.cod_depto)?.NombreDepartamento || "No seleccionado"}</TableCell>

                      <TableCell>
  {(() => {
    const categoriasActivas = (rep.categorias || []).filter(cat => cat.Estado !== 'Inactivo');
    return categoriasActivas.length > 0 ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {categoriasActivas.map((cat, i) => {
          // Construir el label con categoría y puesto si existe
          let label = cat.Descripcion || `Cat ${cat.CategoriaFK || cat.Id}`;

          // Agregar el puesto/cargo si existe
          if (cat.Cargo) {
            label += ` - ${cat.Cargo}`;
          }

          return (
            <Chip
              key={i}
              label={label}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          );
        })}
      </Box>
    ) : "Sin categorías";
  })()}
</TableCell>


                      <TableCell>{rep.estatus}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box m={2}>
                            <Grid container spacing={3}>
                              <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Avatar src={rep.fotografia || "/placeholder-avatar.png"} sx={{ width: 120, height: 120 }} />
                              </Grid>
                              <Grid item xs={12} sm={9}>
                                <Box boxShadow={3} borderRadius={2} p={2} mb={3} bgcolor="background.paper">
                                  <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>Información General</Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}><Typography color="textSecondary"><strong>DNI:</strong> {rep.dni}</Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography color="textSecondary"><strong>Teléfono:</strong> {rep.telefono}</Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography color="textSecondary"><strong>Ubicación de la Finca:</strong> {rep.ubicacion_finca}</Typography></Grid>
                                  </Grid>
                                </Box>
                                <Box boxShadow={3} borderRadius={2} p={2} bgcolor="background.paper">
                                  <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>Detalles</Typography>
                                  <TableContainer component={Paper} elevation={0}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: "bold" }}>Campo</TableCell>
                                          <TableCell sx={{ fontWeight: "bold" }}>Valor</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {[
                                          { label: "Fecha de Certificación", value: rep.fecha_certificacion_punto_acta },
                                          { label: "Clave IHCAFE", value: rep.clave_IHCAFE },
                                          { label: "Reporte de Cosecha", value: rep.reporte_cosecha },
                                          { label: "Fecha de Elección", value: rep.fecha_eleccion },
                                          { label: "Fecha de Vencimiento", value: rep.fecha_vencimiento },
                                          { label: "Domicilio", value: rep.domicilio },
                                          { label: "Observaciones", value: rep.observaciones },
                                          { label: "Cooperativa", value: cooperativas.find(c => c.cod_cooperativa === rep.cod_cooperativa)?.nombre_cooperativa || "No seleccionado" }
                                        ].map((row, i) => (
                                          <TableRow key={i}>
                                            <TableCell>{row.label}</TableCell>
                                            <TableCell>{row.value}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100, 400]}
        component="div"
        count={filteredRepresentantes.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />

      {/* Modal de edición */}
      {selectedRepresentante && (
        <Dialog open maxWidth="md" fullWidth onClose={() => setSelectedRepresentante(null)}>
          <DialogTitle>
            Editar Representante: {selectedRepresentante.nombre_representante}
            <IconButton sx={{ position: "absolute", right: 8, top: 8 }} onClick={() => setSelectedRepresentante(null)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {/* Fotografía */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar src={editedData.fotografiaPreview || editedData.fotografia || "/placeholder-avatar.png"} sx={{ width: 100, height: 100, mb: 1 }} />
              <input accept="image/*" style={{ display: "none" }} id="raised-button-file" type="file" onChange={handleFileChange} />
              <label htmlFor="raised-button-file">
                <Button variant="contained" component="span">Cargar Nueva Fotografía</Button>
              </label>
            </Box>

            {/* SECCIÓN 1: UBICACIÓN */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1, color: '#432918', fontWeight: 'bold' }}>📍 Ubicación</Typography>
            <Box sx={{ bgcolor: '#f8f5f2', p: 2, borderRadius: 2, mb: 2 }}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Departamento</InputLabel>
                <Select value={selectedDepartamento} onChange={(e) => {
                  const newDepartamento = e.target.value;
                  setSelectedDepartamento(newDepartamento);
                  setEditedData(prev => ({ ...prev, cod_depto: newDepartamento, cod_municipio: null }));
                  setMunicipiosFiltrados(municipios.filter(mun => mun.CodigoDepartamento === newDepartamento));
                }} label="Departamento">
                  <MenuItem value=""><em>Seleccione...</em></MenuItem>
                  {departamentos.map((dept) => (
                    <MenuItem key={dept.CodigoDepartamento} value={dept.CodigoDepartamento}>{dept.NombreDepartamento}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal" required>
                <InputLabel>Municipio</InputLabel>
                <Select value={editedData.cod_municipio} onChange={(e) => setEditedData(prev => ({ ...prev, cod_municipio: e.target.value }))}
                  label="Municipio" disabled={!selectedDepartamento}>
                  <MenuItem value=""><em>Seleccione...</em></MenuItem>
                  {municipiosFiltrados.map((mun) => (
                    <MenuItem key={mun.CodigoMunicipio} value={mun.CodigoMunicipio}>{mun.NombreMunicipio}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField label="Domicilio" name="domicilio" value={editedData.domicilio || ""}
                onChange={handleInputChange} fullWidth margin="normal" required />
              
              <TextField label="Ubicación de la Finca" name="ubicacion_finca" value={editedData.ubicacion_finca || ""}
                onChange={handleInputChange} fullWidth margin="normal" required />
            </Box>

            {/* SECCIÓN 2: DATOS PERSONALES */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1, color: '#432918', fontWeight: 'bold' }}>👤 Datos Personales</Typography>
            <Box sx={{ bgcolor: '#f8f5f2', p: 2, borderRadius: 2, mb: 2 }}>
              <TextField label="Nombre Completo" name="nombre_representante" value={editedData.nombre_representante || ""}
                onChange={handleInputChange} fullWidth margin="normal" required />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label="DNI" name="dni" value={editedData.dni || ""}
                    onChange={handleInputChange} fullWidth margin="normal" required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Teléfono" name="telefono" value={editedData.telefono || ""}
                    onChange={handleInputChange} fullWidth margin="normal" required />
                </Grid>
              </Grid>
            </Box>

{/* SECCIÓN 3: ORGANIZACIÓN */}
<Typography variant="h6" sx={{ mt: 2, mb: 1, color: '#432918', fontWeight: 'bold' }}>🏢 Organización</Typography>
<Box sx={{ bgcolor: '#f8f5f2', p: 2, borderRadius: 2, mb: 2 }}>
  <FormControl fullWidth margin="normal" required>
    <InputLabel>Asociación</InputLabel>
    <Select 
      name="cod_asociacion" 
      value={editedData.cod_asociacion || ""} 
      onChange={(e) => {
        const newAsociacion = e.target.value;
        handleInputChange(e);
        // Resetear cooperativa cuando cambia asociación
        setEditedData(prev => ({ ...prev, cod_asociacion: newAsociacion, cod_cooperativa: null }));
      }} 
      label="Asociación"
    >
      <MenuItem value=""><em>Seleccione...</em></MenuItem>
      {asociaciones.map((asoc) => (
        <MenuItem key={asoc.cod_asociacion} value={asoc.cod_asociacion}>{asoc.nombre_abreviado}</MenuItem>
      ))}
    </Select>
  </FormControl>

  <FormControl fullWidth margin="normal" required disabled={!editedData.cod_asociacion}>
    <InputLabel>Cooperativa</InputLabel>
    <Select 
      name="cod_cooperativa" 
      value={editedData.cod_cooperativa || ""} 
      onChange={handleInputChange} 
      label="Cooperativa"
    >
      <MenuItem value=""><em>Seleccione...</em></MenuItem>
      {cooperativas
        .filter(coop => coop.cod_asociacion === editedData.cod_asociacion)
        .map((coop) => (
          <MenuItem key={coop.cod_cooperativa} value={coop.cod_cooperativa}>
            {coop.nombre_cooperativa}
          </MenuItem>
        ))}
    </Select>
  </FormControl>
</Box>
  {/* SECCIÓN 5: FECHAS Y ESTADO */}
           
            <Box sx={{ bgcolor: '#f8f5f2', p: 2, borderRadius: 2, mb: 2 }}>
              <TextField label="Fecha de Certificación" name="fecha_certificacion_punto_acta" type="date"
                value={editedData.fecha_certificacion_punto_acta || ""} onChange={handleInputChange}
                fullWidth margin="normal" InputLabelProps={{ shrink: true }} required />

              <TextField label="Fecha de Elección" name="fecha_eleccion" type="date"
                value={editedData.fecha_eleccion || ""} onChange={handleFechaEleccionChange}
                fullWidth margin="normal" InputLabelProps={{ shrink: true }} required />

              <TextField label="Fecha de Vencimiento" name="fecha_vencimiento" type="date"
                value={editedData.fecha_vencimiento || ""} fullWidth margin="normal"
                InputLabelProps={{ shrink: true }} disabled
                helperText="Se calcula automáticamente (+3 años desde fecha de elección)" />

              <FormControl fullWidth margin="normal">
                <InputLabel>Estado</InputLabel>
                <Select name="estatus" value={editedData.estatus || ""} onChange={(e) => {
                  const nuevoValor = e.target.value;
                  if (nuevoValor === "INHABILITADO") {
                    setEditedData(prev => ({ ...prev, estatus: "INHABILITADO" }));
                  } else if (nuevoValor === "restaurar") {
                    const fechaVenc = new Date(editedData.fecha_vencimiento);
                    const hoy = new Date();
                    const calculado = fechaVenc < hoy ? "VENCIDA" : "VIGENTE";
                    setEditedData(prev => ({ ...prev, estatus: calculado }));
                  }
                }} label="Estado">
                  <MenuItem value={editedData.estatus}>{editedData.estatus}</MenuItem>
                  {editedData.estatus !== "INHABILITADO" ? (
                    <MenuItem value="INHABILITADO">INHABILITAR</MenuItem>
                  ) : (
                    <MenuItem value="restaurar">Restaurar (calcular automáticamente)</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
            {/* SECCIÓN 4: INFORMACIÓN TÉCNICA */}
        
            <Box sx={{ bgcolor: '#f8f5f2', p: 2, borderRadius: 2, mb: 2 }}>
              <TextField label="Clave IHCAFE" name="clave_IHCAFE" value={editedData.clave_IHCAFE || ""}
                onChange={handleInputChange} fullWidth margin="normal" required />
              
              <TextField label="Reporte de Cosecha" name="reporte_cosecha" value={editedData.reporte_cosecha || ""}
                onChange={handleInputChange} fullWidth margin="normal" required />
            </Box>

          

            {/* SECCIÓN 6: DATOS LEGALES (OPCIONAL) */}
      
            <Box sx={{ bgcolor: '#f8f5f2', p: 2, borderRadius: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField label="Número" name="numero" value={editedData.numero || ""} 
                    onChange={handleInputChange} fullWidth margin="normal" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Tomo" name="tomo" value={editedData.tomo || ""} 
                    onChange={handleInputChange} fullWidth margin="normal" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Libro" name="libro" value={editedData.libro || ""} 
                    onChange={handleInputChange} fullWidth margin="normal" />
                </Grid>
              </Grid>

              <TextField label="Fecha Constancia" name="fecha_constancia" type="date" 
                value={editedData.fecha_constancia || ""} onChange={handleInputChange} 
                fullWidth margin="normal" InputLabelProps={{ shrink: true }} />
              
              <TextField label="CONSUCOOP" name="CONSUCOOP" value={editedData.CONSUCOOP || ""} 
                onChange={handleInputChange} fullWidth margin="normal" />
            </Box>

            {/* SECCIÓN 7: OBSERVACIONES */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1, color: '#432918', fontWeight: 'bold' }}>📝 Observaciones</Typography>
            <Box sx={{ bgcolor: '#f8f5f2', p: 2, borderRadius: 2 }}>
              <TextField label="Observaciones" name="observaciones" value={editedData.observaciones || ""}
                onChange={handleInputChange} fullWidth margin="normal" multiline rows={4} 
                placeholder="Agregar notas adicionales sobre el representante..." />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setSelectedRepresentante(null)} color="secondary">Cancelar</Button>
            <Button onClick={handleSaveClick} color="primary" variant="contained" disabled={loading}>
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default MatrizRepresentante;